import { Box, Checkbox, Stack } from "@mui/material";
import { LoadingSpinner } from "@tngtech/tng-ui";
import { ActionNames } from "tng-address-collection-shared/actionNames.ts";
import { useToggleCardSignature } from "../hooks/useToggleCardSignature.ts";
import { useUserHasAction } from "../hooks/useUserHasAction.ts";
import { useTranslator } from "../i18n/translator.ts";
import { DescribedTooltip } from "./DescribedTooltip.tsx";
import { SignaturesDisplay } from "./SignaturesDisplay.tsx";

interface SignatureCheckboxProps {
  cardId: string;
  cardName: string;
  projectId: string;
  isChecked: boolean;
  numberOfSignatures?: number;
  signatures?: Array<{ uidNumber: number; name: string }>;
  onChanged?: (newState: boolean) => void;
  canEditSignatures?: boolean;
  canRemoveOtherSignatures?: boolean;
}

export const SignatureCheckbox = ({
  cardId,
  cardName,
  projectId,
  isChecked,
  numberOfSignatures,
  signatures,
  onChanged,
  canEditSignatures = false,
  canRemoveOtherSignatures = false,
}: SignatureCheckboxProps) => {
  const userHasAction = useUserHasAction(projectId);
  const translator = useTranslator();
  const { isPending, mutate } = useToggleCardSignature({
    cardId,
    onChanged,
    projectId,
  });
  const canSign = userHasAction(ActionNames.signCard);
  // Users who lost access to the project may still remove their own signature.
  const canRemoveSignature = userHasAction(ActionNames.removeCardSignature);
  const canOnlyRemoveSignature = isChecked && canRemoveSignature && !canSign;

  const checkbox = (ariaDescribedById?: string) => (
    <Checkbox
      checked={isChecked}
      disabled={isChecked ? !canRemoveSignature : !canSign}
      slotProps={{
        input: {
          "aria-label": `${translator("signCard")}: ${cardName}`,
          "aria-describedby": ariaDescribedById,
        },
      }}
      onClick={() => {
        if (!isPending) mutate(isChecked);
      }}
    />
  );
  return (
    <Stack direction="row" justifyContent="start" alignItems="center">
      {isPending ? (
        <Box p={1}>
          <LoadingSpinner width={24} height={24} />
        </Box>
      ) : canOnlyRemoveSignature ? (
        <DescribedTooltip
          title={translator("signatureOnlyRemovable")}
          arrow
          placement="auto-start"
          renderTrigger={checkbox}
        />
      ) : (
        checkbox()
      )}
      <SignaturesDisplay
        cardId={cardId}
        projectId={projectId}
        signatures={signatures}
        numberOfSignatures={numberOfSignatures}
        canEditSignatures={canEditSignatures}
        canRemoveOtherSignatures={canRemoveOtherSignatures}
      />
    </Stack>
  );
};
